import React, { useContext, useEffect, useState } from 'react';
import { CustomDialog } from '../common/custom-dialog';
import { useTranslation } from '../../../../hooks/useTranslation';
import IconBolt from '../../../../static/icon-bolt.svg';
import {
  Box,
  Divider,
  Grid,
  Link,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import colors from '../../../../themes/main-colors';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useOutageEventsDetails } from '../../hooks/useOutageEventsDetails';
import {
  OutageDetailsByCounty,
  OutageDetailsByCity,
  ZipCodeInfo,
} from '../../../../__generated__/pge-types';
import { useIsMobile } from '../../../../util/style-utils';
import { useReportOutageApi } from '../../hooks/useReportOutageApi';
import { TransitionContext } from '../../../../providers/TransitionProvider';
import { useGetOutagesByCountyOrZip } from '../../hooks/useGetOutagesByCountyOrZip';
import Backdrop from '../../../backdrop';
import { useOutageMapContext } from '../../providers/OutageMapProvider';
import {
  ShowByType,
  useOutageMapOptions,
} from '../../providers/OutageMapOptionsProvider';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';
import { useGetOutagesByCounty } from '../../hooks/useGetOutagesByCounty';

const useStyles = makeStyles((theme: Theme) => ({
  helpIcon: {
    fontSize: 30,
    color: colors.white,
  },
  font14: {
    fontSize: 14,
  },
  fontBold: {
    fontWeight: 600,
  },
  gridContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    background: colors.white,
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  boxContainer: {
    padding: '1rem',
    backgroung: colors.aliceBlue,
  },
  card: {
    backgroundColor: colors.white,
    padding: theme.spacing(1.25),
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
  },
  value: {
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  label: {
    fontSize: '1rem',
  },
  link: {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  tabComponent: {
    display: 'flex',
    marginTop: '1rem',
    textAlign: 'center',
  },
  tabButton: {
    width: '50%',
    cursor: 'pointer',
  },
  tabButtonActive: {
    background: colors.white,
    fontWeight: 600,
    padding: '0.8rem',
  },
  tabButtonInactive: {
    background: colors.gray300,
    padding: '0.8rem',
    fontWeight: 600,
    color: colors.sparkBlue,
  },
  tableStyles: {
    background: colors.white,
    padding: '1rem 1rem 0 1rem',
    overflowY: 'auto',
    height: '33vh',
    scrollbarWidth: 'none',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
  },
  tableData: {
    padding: '1rem 0',
  },
  cityStyles: {
    textTransform: 'uppercase',
    color: colors.sparkBlue,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

interface ReportOutageByCountyCityProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  defaultTab?: number;
}

export const ReportOutageByCountyCity = ({
  open,
  setOpen,
  defaultTab = 0,
}: ReportOutageByCountyCityProps) => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const tabs = [t('COUNTY'), t('ZIP_CODE')];
  const [active, setActive] = useState(defaultTab);
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      setActive(defaultTab);
    }
  }, [open, defaultTab]);
  const {
    activeOutages,
    customersAffected,
    customersWithPower,
    totalPlannedOutages,
  } = useOutageEventsDetails();

  const { getOutagesByCounty } = useReportOutageApi();
  // const { getOutagesByCity } = useReportOutageApi();
  const [countyData, setCountyData] = useState<OutageDetailsByCounty[] | null>(
    null,
  );
  const [countyError, setCountyError] = useState<any>(null);
  // const [cityData, setCityData] = useState<OutageDetailsByCity[] | null>(null);
  const [zipCodeData, setZipCodeData] = useState<ZipCodeInfo[] | null>(null);
  // const [cityLoading, setCityLoading] = useState(false);
  // const [cityError, setCityError] = useState<any>(null);
  const { setState: updateTransition } = useContext(TransitionContext);
  const {
    outages: outagesByCountyOrZip,
    loading: outagesByCountyOrZipLoading,
  } = useGetOutagesByCountyOrZip();
  const {
    outages: outagesByCounty,
    loading: outagesByCountyLoading,
  } = useGetOutagesByCounty();
  const { mapInstance } = useOutageMapContext();
  const {
    zipOutagesCenters,
    countyOutagesCenters,
    setShowBy,
    setOutageToggles,
  } = useOutageMapOptions();
  const { setPage } = useOutageResponsivePageTracker();

  useEffect(() => {
    if (outagesByCountyOrZip && outagesByCountyOrZip.length) {
      setZipCodeData(
        outagesByCountyOrZip?.flatMap(county => county?.zipCodeInfo),
      );
    }
  }, [outagesByCountyOrZip]);

  useEffect(() => {
    if (outagesByCounty && outagesByCounty.length) {
      setCountyData(outagesByCounty);
    }
  }, [outagesByCounty]);

  // React.useEffect(() => {
  //   const mounted = true;
  //   const fetchCities = async () => {
  //     try {
  //       updateTransition({ open: true });
  //     } catch (e) {}
  //     setCityLoading(true);
  //     try {
  //       const res = await getOutagesByCity({});
  //       if (mounted) {
  //         setCityData(res?.data ?? null);
  //         try {
  //           updateTransition({ open: false });
  //         } catch (e) {}
  //       }
  //     } catch (err) {
  //       if (mounted) {
  //         setCityError(err);
  //       }
  //     } finally {
  //       if (mounted) {
  //         setCityLoading(false);
  //       }
  //     }
  //   };
  //   if (open && active === 0 && cityData === null) {
  //     void fetchCities();
  //   }
  // }, [open, active, cityData]);

  const onZipSelect = (zip: string) => {
    if (mapInstance && zipOutagesCenters.length > 0) {
      const selectedZip = zipOutagesCenters.find(
        outage => outage.zipCode === zip,
      );
      mapInstance?.flyTo({
        center: selectedZip?.center,
        zoom: 11,
        duration: 0,
        essential: true,
      });
    }
    setShowBy(ShowByType.ZIP);
    setPage(ResponsivePageView.Map);
    setOpen(false);
  };

  const onCountySelect = (county: string) => {
    if (mapInstance && countyOutagesCenters.length > 0) {
      const selectedCounty = countyOutagesCenters.find(
        outage => outage.county === county,
      );
      mapInstance?.flyTo({
        center: selectedCounty?.center,
        zoom: 10,
        duration: 0,
        essential: true,
      });
    }
    setShowBy(ShowByType.COUNTY);
    setPage(ResponsivePageView.Map);
    setOpen(false);
  };
  const renderTableData = (location: string) => (
    <div>
      <Grid
        container
        alignItems="center"
        spacing={1}
        className={classes.tableData}
      >
        <Grid item xs={7}>
          <Typography className={clsx(classes.font14, classes.fontBold)}>
            {t('LOCATION')}
          </Typography>
        </Grid>
        <Grid item xs={2} hidden={location === 'zip'}>
          <Typography
            align="right"
            className={clsx(classes.font14, classes.fontBold)}
          >
            {t('OUTAGES')}
          </Typography>
        </Grid>
        <Grid item xs={location === 'zip' ? 5 : 3}>
          <Typography
            align="right"
            className={clsx(classes.font14, classes.fontBold)}
          >
            {t('CUSTOMERS')}
          </Typography>
        </Grid>
      </Grid>

      {location === 'zip'
        ? zipCodeData?.map((row, idx) => (
            <React.Fragment key={row.zipCode + idx}>
              <Divider />
              <Grid
                container
                alignItems="center"
                spacing={1}
                style={{ marginTop: 12, marginBottom: 12 }}
              >
                <Grid item xs={7}>
                  <Link
                    onClick={() => {
                      onZipSelect(row.zipCode);
                    }}
                  >
                    <Typography
                      className={clsx(classes.fontBold, classes.cityStyles)}
                    >
                      {row.zipCode}
                    </Typography>
                  </Link>
                </Grid>
                {/* TODO: Will unhide when the API is ready */}
                <Grid item xs={2} hidden>
                  <Typography align="right" className={classes.value}>
                    {row.outages.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={location === 'zip' ? 5 : 3}>
                  <Typography align="right" className={classes.value}>
                    {row.totalCustomersEffected.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </React.Fragment>
          ))
        : countyData?.map((row, idx) => (
            <React.Fragment key={row.county + idx}>
              <Divider />
              <Grid
                container
                alignItems="center"
                spacing={1}
                style={{ marginTop: 12, marginBottom: 12 }}
              >
                <Grid item xs={7}>
                  <Link
                    onClick={() => {
                      onCountySelect(row.county);
                    }}
                  >
                    <Typography
                      className={clsx(classes.fontBold, classes.cityStyles)}
                    >
                      {row.county}
                    </Typography>
                  </Link>
                </Grid>
                <Grid item xs={2}>
                  <Typography align="right" className={classes.value}>
                    {row.outages.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography align="right" className={classes.value}>
                    {row.totalCustomersImpacted.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </React.Fragment>
          ))}
    </div>
  );

  return (
    <>
      {(outagesByCountyLoading || outagesByCountyOrZipLoading) && (
        <Backdrop forceOpen />
      )}
      <CustomDialog
        open={open}
        onClose={handleClose}
        title={t('OUTAGE_BY_CITY_ZIP')}
        titleIcon={<IconBolt className={classes.helpIcon} />}
      >
        <Box>
          <Grid container className={classes.gridContainer}>
            <Grid item>
              <Typography className={clsx(classes.font14, classes.fontBold)}>
                {richT('DATE_LAST_UPDATED', {
                  DATE_UPDATED: dayjs(new Date(), 'MMMM D, YYYY h:mm a')
                    .format('MMM D @ h:mm a')
                    .replace('May', 'May')
                    .replace('am', 'AM')
                    .replace('pm', 'PM'),
                })}
              </Typography>{' '}
            </Grid>
            <Grid item>
              <Typography className={classes.font14}>
                {t('INFORMATION_UPDATED')}
              </Typography>
            </Grid>
          </Grid>
          <Box className={classes.boxContainer}>
            <Grid container spacing={1}>
              <Grid item xs={isMobile ? 6 : 3}>
                <Box className={classes.card}>
                  <Typography className={classes.value}>
                    {customersWithPower}
                  </Typography>
                  <Typography className={classes.label}>
                    {t('TOTAL_PERCENT_CUSTOMERS_AFFECTED')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={isMobile ? 6 : 3}>
                <Box className={classes.card}>
                  <Typography className={classes.value}>
                    {customersAffected.toLocaleString()}
                  </Typography>
                  <Typography className={classes.label}>
                    {t('CUSTOMERS_AFFECTED')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={isMobile ? 6 : 3}>
                <Box className={classes.card}>
                  <Typography className={classes.value}>
                    {activeOutages.toLocaleString()}
                  </Typography>
                  <Link
                    className={clsx(classes.label, classes.link)}
                    onClick={() => {
                      setShowBy(ShowByType.DEFAULT);
                      setOutageToggles({
                        planned: true,
                        unplanned: true,
                        psps: false,
                      });
                      setPage(ResponsivePageView.Map);
                      setOpen(false);
                    }}
                  >
                    {t('ACTIVE_OUTAGES')}
                  </Link>
                </Box>
              </Grid>
              <Grid item xs={isMobile ? 6 : 3}>
                <Box className={classes.card}>
                  <Typography className={classes.value}>
                    {totalPlannedOutages?.toLocaleString()}
                  </Typography>
                  <Link
                    className={clsx(classes.label, classes.link)}
                    onClick={() => {
                      setShowBy(ShowByType.DEFAULT);
                      setOutageToggles({
                        planned: true,
                        unplanned: false,
                        psps: false,
                      });
                      setPage(ResponsivePageView.Map);
                      setOpen(false);
                    }}
                  >
                    {t('PLANNED_OUTAGES')}
                  </Link>
                </Box>
              </Grid>
            </Grid>

            <div>
              <div className={classes.tabComponent}>
                {tabs.map((tab, idx) => (
                  <div
                    key={tab}
                    className={clsx(
                      classes.tabButton,
                      active === idx
                        ? classes.tabButtonActive
                        : classes.tabButtonInactive,
                    )}
                    onClick={() => setActive(idx)}
                  >
                    {tab}
                  </div>
                ))}
              </div>
              <div className={classes.tableStyles}>
                {active === 0 ? (
                  <div>{renderTableData('county')}</div>
                ) : (
                  <div>{renderTableData('zip')}</div>
                )}
              </div>
            </div>
          </Box>
        </Box>
      </CustomDialog>
    </>
  );
};
